import { useCallback, useRef } from 'react'

import { handleNotAwaitedPromise } from '@valian/react-kitchen-sink'

import { playOnce } from './playOnce'

export type NotificationSoundType = 'teamMessage' | 'transportRequest' | 'support'

const EXPECTED_PLAY_FAILURES = new Set(['NotAllowedError', 'TimeoutError', 'AbortError'])

const SOUND_FILES: Record<NotificationSoundType, string> = {
  teamMessage: '/alert_new_message.mp3',
  transportRequest: '/alert_transport_request_changed.mp3',
  support: '/alert_support.mp3',
}

export function useNotificationSound() {
  const audioRefs = useRef<Partial<Record<NotificationSoundType, HTMLAudioElement>>>({})

  const playNotificationSound = useCallback((type: NotificationSoundType, eventKey: string) => {
    let audio = audioRefs.current[type]
    if (!audio) {
      audio = new Audio(SOUND_FILES[type])
      audio.preload = 'auto'
      audioRefs.current[type] = audio
    }
    const sound = audio
    handleNotAwaitedPromise(
      playOnce(`${type}:${eventKey}`, (signal) => {
        signal.addEventListener('abort', () => {
          sound.pause()
        })
        sound.currentTime = 0
        return sound.play()
      }).catch((error: unknown) => {
        // A refused or stalled play only means another tab plays the sound, or the browser keeps it muted.
        if (!(error instanceof DOMException && EXPECTED_PLAY_FAILURES.has(error.name))) {
          throw error
        }
      })
    )
  }, [])

  return { playNotificationSound }
}
